import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { assertHasAuthorization } from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import { uzoviAddEditSchema } from '../../../../common/validation/uzovi';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { CancelButton, FormContainer, Header, Loading, SaveSubmit } from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateField } from '../../fields/Date';
import { TextField } from '../../fields/Text';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type IUzoviAddEditProps = IWithRouterProps;

interface IUzovi {
    code: string;
    endDate: string | null;
    id?: string;
    name: string;
    startDate: string;
}

interface IUzoviAddEditState {
    errors: ValidationErrors;
    isSaving: boolean;
    uzovi: IUzovi;
}

export class InnerUzoviAddEdit extends Component<IUzoviAddEditProps, IUzoviAddEditState> {
    constructor(props: IUzoviAddEditProps) {
        super(props);

        assertHasAuthorization(ManagerAuthorization.uzovisAddEdit);

        this.state = {
            uzovi: {
                code: '',
                name: '',
                startDate: '',
                endDate: null,
            },
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            this.load();
        }
    }

    public render() {
        return (
            <FixedWidthTemplate>
                <Header
                    content={
                        translateKey('uzovi.uzovi') +
                        ' ' +
                        (this.props.router.params.id ? translateKey('common.edit') : translateKey('common.new'))
                    }
                />

                <Loading
                    item={this.state.uzovi}
                    loaded={(uzovi) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <FormContextProvider
                                value={{
                                    placeholderGroupName: '',
                                    item: this.state.uzovi,
                                    errors: this.state.errors,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                }}
                            >
                                <TextField
                                    fieldKey="code"
                                    isRequired
                                    label={translateKey('uzovi.code')}
                                    onDoValidate={async () => await this.validateCode()}
                                    placeholder={translateKey('uzovi.code')}
                                />
                                <TextField
                                    fieldKey="name"
                                    isRequired
                                    label={translateKey('common.name')}
                                    placeholder={translateKey('common.name')}
                                />
                                <DateField
                                    fieldKey="startDate"
                                    isRequired
                                    label={translateKey('common.startDate')}
                                    placeholder={translateKey('common.startDate')}
                                />
                                <DateField
                                    fieldKey="endDate"
                                    label={translateKey('common.endDate')}
                                    placeholder={translateKey('common.endDate')}
                                />
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to="/uzovis"
                            />
                            <SaveSubmit disabled={this.state.isSaving} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async validateCode() {
        const code = this.state.uzovi.code;
        const minimumLength = 4;

        if (code.length < minimumLength && code.match(/[0-9]+/)) {
            const updatedCode = code.padStart(minimumLength, '0');
            await this.handleChange('code', updatedCode);
        }

        await this.validateOne('code');
    }

    private async handleChange<K extends keyof IUzovi>(name: K, value: IUzovi[K]) {
        await this.setState(({ uzovi }) => ({ uzovi: { ...uzovi, [name]: value } }));
    }

    private async validateOne(name: keyof IUzovi) {
        await this.setState(({ uzovi, errors }) => ({
            errors: updateValidationErrorsForOne(uzoviAddEditSchema, uzovi, errors, name),
        }));
    }

    private async load() {
        const url = '/api/uzovis/' + this.props.router.params.id;
        const result = await fetchJson(url, 'GET');
        await this.setState({ uzovi: result.json });
    }

    private async save() {
        let url = '/api/uzovis/';

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        }

        const { errors, item } = validate(uzoviAddEditSchema, this.state.uzovi);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        await fetchJson(url, 'POST', item);
        let returnUrl = '/uzovis';
        const searchParams = this.props.router.searchParams;
        const returnPage = searchParams.get('returnPage');
        if (returnPage) {
            returnUrl += `?page=${returnPage}`;
        }
        this.props.router.navigate(returnUrl);
    }
}

export const UzoviAddEdit = withRouter(InnerUzoviAddEdit);
